// web/src/lib/webrtc/useWebRTCCall.ts

import { useState, useRef, useCallback, useEffect } from "react";
import type { WebRTCCallAdapter, WebRTCCallStatus, VoiceTokenResponse } from "./types";
import { createWebRTCAdapter } from "./factory";

async function fetchVoiceToken(): Promise<VoiceTokenResponse> {
  const res = await fetch("/api/communications/voice-token", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: "Failed to get voice token" } }));
    throw new Error(err.error?.message ?? "Failed to get voice token");
  }
  return res.json();
}

export function useWebRTCCall() {
  const [status, setStatus] = useState<WebRTCCallStatus>("idle");
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [provider, setProvider] = useState<"twilio" | "3cx" | null>(null);
  const adapterRef = useRef<WebRTCCallAdapter | null>(null);

  const startCall = useCallback(async (toNumber: string) => {
    setError(null);
    setStatus("initializing");

    try {
      // 1. Get voice token from backend
      const tokenResponse = await fetchVoiceToken();
      setProvider(tokenResponse.provider);

      // 2. Create and initialize adapter
      const adapter = await createWebRTCAdapter(tokenResponse.provider);
      adapter.onStatusChange((newStatus) => {
        setStatus(newStatus);
      });
      adapter.onError((err) => {
        setError(err.message);
        setStatus("error");
      });

      await adapter.initialize(tokenResponse.token, tokenResponse.config);

      // 3. Make the call
      await adapter.makeCall(toNumber);
      adapterRef.current = adapter;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to start call";
      setError(message);
      setStatus("error");
    }
  }, []);

  const endCall = useCallback(async () => {
    if (adapterRef.current) {
      await adapterRef.current.hangUp();
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (adapterRef.current) {
      const newMuted = !isMuted;
      adapterRef.current.mute(newMuted);
      setIsMuted(newMuted);
    }
  }, [isMuted]);

  const toggleHold = useCallback(() => {
    if (adapterRef.current) {
      const newHold = !isOnHold;
      adapterRef.current.hold(newHold);
      setIsOnHold(newHold);
    }
  }, [isOnHold]);

  const sendDtmf = useCallback((digit: string) => {
    if (adapterRef.current) {
      adapterRef.current.sendDtmf(digit);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      adapterRef.current?.destroy();
    };
  }, []);

  return {
    status,
    isMuted,
    isOnHold,
    error,
    provider,
    startCall,
    endCall,
    toggleMute,
    toggleHold,
    sendDtmf,
  };
}
